import {
  RadialBarChart, RadialBar, PolarAngleAxis,
  RadarChart, Radar, PolarGrid,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ScatterChart, Scatter, ZAxis, ReferenceLine,
  LineChart, Line, ResponsiveContainer, Tooltip,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Radio, CircleDashed } from 'lucide-react';

// Three-value source badge. The label is DATA-DRIVEN by `source` - a box flips to a
// live/verified badge automatically once its source is wired; no text is hardcoded per box.
//
// It used to be three English literals here, while ProvenanceLabel printed the raw machine
// value for the SAME claim - "Modeled . not fact" on an evidence card and "modeled-not-fact"
// on a badge. One claim written two ways is what CLAUDE.md 5 forbids, so both read one key.
//
// The VALUE behind each key is the machine token, identical in en/fa/es, matching pathB.lane*
// and the three labels CLAUDE.md 1 names. Deliberate rather than untranslated: the token IS
// the label, and somebody auditing the Persian output has to see the same word as somebody
// auditing the English. The keys exist so that stays a decision instead of a hardcode.
const SOURCE_META = {
  'corpus-grounded': { cls: 'bg-success-soft text-success border-success-border', Icon: ShieldCheck, labelKey: 'provenance.corpusGrounded' },
  // Internal source key stays 'live-data' - the code's own vocabulary, and with no live
  // connector wired this branch does not render today either way.
  'live-data': { cls: 'bg-info-soft text-info border-info-border', Icon: Radio, labelKey: 'provenance.liveFetched' },
  'modeled-not-fact': { cls: 'bg-warning-soft text-warning border-warning-border', Icon: CircleDashed, labelKey: 'provenance.modeledNotFact' },
};

// testId lets ProvenanceLabel reuse this badge without moving the hook its tests look for.
export function SourceBadge({ source, testId }) {
  const { t } = useTranslation();
  const meta = SOURCE_META[source] || SOURCE_META['modeled-not-fact'];
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.cls}`}
      data-testid={testId || `source-badge-${source || 'modeled-not-fact'}`}
    >
      <Icon className="h-3 w-3" />
      {t(meta.labelKey)}
    </span>
  );
}

function scoreColor(s) {
  if (s >= 80) return '#16a34a';
  if (s >= 60) return '#0284c7';
  if (s >= 40) return '#d97706';
  return '#dc2626';
}

// Fixed-size radial gauge — uses explicit pixel dimensions (no ResponsiveContainer) so it
// never logs the Recharts width(-1) warning.
export function ScoreGauge({ score = 0, size = 160, testId = 'score-gauge' }) {
  const val = Math.max(0, Math.min(100, Math.round(score)));
  const data = [{ name: 'score', value: val, fill: scoreColor(val) }];
  return (
    <div className="relative shrink-0" dir="ltr" style={{ width: size, height: size }} data-testid={testId}>
      <RadialBarChart width={size} height={size} innerRadius="72%" outerRadius="100%" data={data} startAngle={90} endAngle={-270}>
        <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
        <RadialBar background={{ fill: '#f1f5f9' }} dataKey="value" cornerRadius={20} />
      </RadialBarChart>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-data text-3xl font-bold" style={{ color: scoreColor(val) }}>{val}</span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 100</span>
      </div>
    </div>
  );
}

// All responsive charts below use width="100%" + an explicit numeric height (never "100%")
// and a min-w-0 parent so Recharts can measure a real width on first paint.
//
// dir="ltr" on every chart wrapper, deliberately: Recharts computes tick/label/point geometry
// assuming LTR, and under the app's RTL root the SVG text anchors flip — every axis tick,
// point and title lands off its grid position (Ali, 2026-08-03). A chart is a coordinate
// system, not prose; the Persian strings inside it render fine either way.
export function ScoreRadar({ data = [], testId = 'score-radar' }) {
  if (!data.length) return null;
  const rows = data.map((d) => ({ criterion: d.criterion, score: d.score }));
  return (
    <div className="w-full min-w-0" dir="ltr" data-testid={testId}>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={rows} outerRadius="65%" margin={{ top: 16, right: 32, bottom: 24, left: 32 }}>
          <PolarGrid stroke="#e2e8f0" />
          <PolarAngleAxis dataKey="criterion" tick={{ fontSize: 11, fill: '#475569' }} />
          <Radar dataKey="score" stroke="#0f172a" fill="#0f172a" fillOpacity={0.15} />
          <Tooltip />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SubScoreBars({ data = [], testId = 'subscore-bars' }) {
  if (!data.length) return null;
  const rows = data.map((d) => ({ criterion: d.criterion, score: d.score, fill: scoreColor(d.score) }));
  const height = Math.max(160, rows.length * 46 + 28);
  return (
    <div className="w-full min-w-0" dir="ltr" data-testid={testId}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, left: 8, right: 24, bottom: 24 }}>
          <CartesianGrid horizontal={false} stroke="#f1f5f9" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="criterion" width={150} tick={{ fontSize: 11, fill: '#475569' }} />
          <Tooltip />
          <Bar dataKey="score" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RiskMatrix({ items = [], testId = 'risk-matrix' }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  const rows = items.map((r) => ({ x: r.likelihood, y: r.impact, risk: r.risk }));
  return (
    <div className="w-full min-w-0" dir="ltr" data-testid={testId}>
      <ResponsiveContainer width="100%" height={280}>
        <ScatterChart margin={{ top: 16, right: 24, bottom: 40, left: 8 }}>
          <CartesianGrid stroke="#f1f5f9" />
          <XAxis type="number" dataKey="x" name={t('market.axisLikelihood')} domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} label={{ value: t('market.axisLikelihood'), position: 'insideBottom', offset: -12, fontSize: 11 }} />
          <YAxis type="number" dataKey="y" name={t('market.axisImpact')} domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} label={{ value: t('market.axisImpact'), angle: -90, position: 'insideLeft', fontSize: 11 }} />
          <ZAxis range={[120, 121]} />
          <ReferenceLine x={2.5} stroke="#e2e8f0" />
          <ReferenceLine y={2.5} stroke="#e2e8f0" />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v, n, p) => [p?.payload?.risk, t('market.axisRisk')]} />
          <Scatter data={rows} fill="#dc2626" />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DemandTrend({ data = [], testId = 'demand-trend' }) {
  if (!data.length) return null;
  return (
    <div className="w-full min-w-0" dir="ltr" data-testid={testId}>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, left: 8, right: 24, bottom: 30 }}>
          <CartesianGrid stroke="#f1f5f9" />
          <XAxis dataKey="period" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
